/**
 * Centralized input management. Maps raw input events to game actions.
 * Provides rebindable controls and clean separation of input from game logic.
 */
export class InputManager {
    /** @type {Map<string, boolean>} */
    #actions = new Map();

    /** @type {Map<string, Set<string>>} */
    #bindings = new Map();

    /** @type {Map<string, boolean>} */
    #previousState = new Map();

    constructor() {
        // Default key bindings
        this.bind('forward', ['KeyW', 'ArrowUp']);
        this.bind('backward', ['KeyS', 'ArrowDown']);
        this.bind('left', ['KeyA', 'ArrowLeft']);
        this.bind('right', ['KeyD', 'ArrowRight']);
        this.bind('jump', ['Space']);
        this.bind('poop', ['KeyQ']);
        this.bind('toggleFlamethrower', ['KeyF']);
        this.bind('aim', ['KeyC']);
        this.bind('resetCamera', ['KeyZ']);
        this.bind('toggleRun', ['CapsLock']);

        // Set up event listeners
        this.#setupEventListeners();
    }

    /**
     * Set up keyboard event listeners
     */
    #setupEventListeners() {
        document.addEventListener('keydown', (event) => {
            if (event.repeat) return;
            // CapsLock fires keyup immediately on macOS, so treat it as a
            // toggle: each keydown flips the bound action's state
            if (event.code === 'CapsLock') {
                for (const [action, keys] of this.#bindings.entries()) {
                    if (keys.has('CapsLock')) {
                        const current = this.#actions.get(action) ?? false;
                        this.#actions.set(action, !current);
                    }
                }
                return;
            }
            this.#handleKeyEvent(event.code, true);
        });

        document.addEventListener('keyup', (event) => {
            // CapsLock keyup is ignored — handled as toggle on keydown
            if (event.code === 'CapsLock') return;
            this.#handleKeyEvent(event.code, false);
        });
    }

    /**
     * Handle a keyboard event
     * @param {string} keyCode - The key code
     * @param {boolean} pressed - Whether the key is pressed or released
     */
    #handleKeyEvent(keyCode, pressed) {
        // Find all actions bound to this key
        for (const [action, keys] of this.#bindings.entries()) {
            if (keys.has(keyCode)) {
                this.#actions.set(action, pressed);
            }
        }
    }

    /**
     * Bind an action to one or more key codes
     * @param {string} action - The action name
     * @param {string[]} keyCodes - Array of key codes
     */
    bind(action, keyCodes) {
        if (!this.#bindings.has(action)) {
            this.#bindings.set(action, new Set());
            this.#actions.set(action, false);
            this.#previousState.set(action, false);
        }

        const keySet = this.#bindings.get(action);
        if (!keySet) {
            return;
        }

        keyCodes.forEach(keyCode => keySet.add(keyCode));
    }

    /**
     * Unbind an action
     * @param {string} action - The action name
     */
    unbind(action) {
        this.#bindings.delete(action);
        this.#actions.delete(action);
        this.#previousState.delete(action);
    }

    /**
     * Check if an action is currently pressed
     * @param {string} action - The action name
     * @returns {boolean}
     */
    isPressed(action) {
        return this.#actions.get(action) ?? false;
    }

    /**
     * Check if an action was just pressed this frame (button press)
     * @param {string} action - The action name
     * @returns {boolean}
     */
    wasJustPressed(action) {
        const current = this.#actions.get(action) ?? false;
        const previous = this.#previousState.get(action) ?? false;
        return current && !previous;
    }

    /**
     * Check if an action was just released this frame
     * @param {string} action - The action name
     * @returns {boolean}
     */
    wasJustReleased(action) {
        const current = this.#actions.get(action) ?? false;
        const previous = this.#previousState.get(action) ?? false;
        return !current && previous;
    }

    /**
     * Update the input state. Should be called once per frame.
     */
    update() {
        // Update previous state for edge detection
        for (const [action, state] of this.#actions.entries()) {
            this.#previousState.set(action, state);
        }
    }

    /**
     * Get all current bindings
     * @returns {Map<string, Set<string>>}
     */
    getBindings() {
        return new Map(this.#bindings);
    }

    /**
     * Clear all bindings
     */
    clearAllBindings() {
        this.#bindings.clear();
        this.#actions.clear();
        this.#previousState.clear();
    }
}
